import { useAppStore } from "@/stores";
import useSWR from "swr";
import { Link } from "wouter";
import {
  getAll,
  getDeals,
  getDealstageAvgTimesByCompanyId,
  // hasHubspotToken,
} from "@/lib/supabase";
import { Loader } from "@/components/Loader";
// import { LoadingError } from "@/components/LoadingError";
import {
  startOfQuarter,
  endOfYear,
  subQuarters,
  addMonths,
  isAfter,
  // subMonths,
  // startOfMonth,
  endOfMonth,
  // isBefore,
} from "date-fns";
import {
  getRiskIndicatorDescription,
  mapRiskName,
  type RiskIndicator,
} from "@/revenue-intelligence/lib/risk-contracts";
import { Fragment, useState } from "react";
import { useCanonicalForecast } from "../expected-close-v2/useCanonicalForecast";
import { getDealOwner } from "@revelate/utils";
import { getAPI } from "@/lib/api";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import {
  evaluateRiskForecast,
  evaluateRiskPipelineCoverage,
  evaluateRiskPipelineRisk,
  formatCurrencyValue,
  prepareDealsForRiskAssessment,
} from "../lib/risk";
import {
  getCompanyTarget,
} from "../lib/companies";
import { Switch } from "@/components/ui/switch";
import {
  RiskHighlightCard,
  RiskHighlightBadge,
  RiskHighlightBadges,
} from "../components/RiskHighlightCard";
import {
  ForecastChart,
  PipelineCoverageChart,
  PipelineRiskBubbleChart,
} from "../components/charts";
import { AIInsightCard } from "../components/AIInsightCard";
import { SectionCard } from "../components/SectionCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AtRiskDealsDataTable } from "../components/AtRiskDealsDataTable";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePostHog } from "@posthog/react";
import { usePageTimeTracking } from "@/lib/posthog/usePageTimeTracking";

// Important dates
const now = new Date();
// const startOfThisQuarter = startOfQuarter(now);
// const startOfPrevQuarter = startOfQuarter(subQuarters(now, 1));
const startOf2QuartersAgo = startOfQuarter(subQuarters(now, 2));
const endOfThisYear = endOfYear(now);
// const startOfThisMonth = startOfMonth(now);
// const startOfLastMonth = startOfMonth(subMonths(now, 1));
// const endOf3Months = endOfMonth(addMonths(now, 2));
const endOf6Months = endOfMonth(addMonths(now, 5));

type ForecastTimePeriod =
  | "prev_quarter"
  | "this_quarter"
  | "next_quarter"
  | "next_6_months"
  | "this_year"
  ;

export function RevenueIntelligenceDashboardPage() {
  const [forecastTimePeriod, setForecastTimePeriod] = useState<ForecastTimePeriod>("this_year");
  const [isForecastAccumulated, setIsForecastAccumuated] = useState<boolean>(false);
  const [bubbleChartView, setBubbleChartView] = useState<"largest" | "riskiest">("largest");
  const [globalSelectedPipeline, setGlobalSelectedPipeline] = useState<string>("all");
  const [globalSelectedOwner, setGlobalSelectedOwner] = useState<string>("all");
  const canonicalForecast = useCanonicalForecast(forecastTimePeriod, isForecastAccumulated, globalSelectedPipeline, globalSelectedOwner);
  const [isRevenueAtRiskDialogOpen, setIsRevenueAtRiskDialogOpen] = useState<boolean>(false);
  const [isPipelineConcentrationDialogOpen, setIsPipelineConcentrationDialogOpen] = useState<boolean>(false);
  const [revenueAtRiskViewingRisk, setRevenueAtRiskViewingRisk] = useState<RiskIndicator | null>(null);

  const { currentUser, companyCurrency } = useAppStore((state) => state);

  const posthog = usePostHog();
  usePageTimeTracking("l1", posthog);

  // const {
  //   data: hasHsToken,
  //   error: hasHsTokenError,
  //   isLoading: isLoadingHasHsToken,
  // } = useSWR(
  //   [
  //     'query:hasHubspotToken',
  //     currentUser ? currentUser?.company_id.toString() : null,
  //   ],
  //   ([_, companyId]) => hasHubspotToken(companyId),
  //   {
  //     revalidateOnFocus: false,
  //     revalidateOnReconnect: false,
  //   }
  // );

  const startOfForecastTimePeriod = startOf2QuartersAgo;

  const endOfForecastTimePeriod = isAfter(endOfThisYear, endOf6Months)
    ? endOfThisYear
    : endOf6Months;

  const {
    data: allOpenDeals,
    // error: allOpenDealsError,
    // isLoading: isLoadingAllOpenDeals,
  } = useSWR(
    [
      'query:allOpenDeals',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getDeals(companyId, {
      onlyOpen: true,
      nonZeroValue: true,
      includeEmails: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: dealsForForecast,
    // error: dealsForForecastError,
    // isLoading: isLoadingDealsForForecast,
  } = useSWR(
    [
      'query:dealsForForecast',
      currentUser ? currentUser?.company_id.toString() : null,
      startOfForecastTimePeriod.toISOString(),
      endOfForecastTimePeriod.toISOString(),
    ],
    ([_, companyId, from, to]) => getDeals(companyId, {
      dateRange: { from, to },
      nonZeroValue: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: currencies,
    // error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useSWR(
    [
      'query:currencies',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAll("currencies", companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const {
    data: pipelines,
    // error: pipelinesError,
    // isLoading: isLoadingPipelines,
  } = useSWR(
    [
      'query:pipelines',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => getAPI(`providers/hubspot/pipelines/${companyId}`),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const pipelinesForSelect = pipelines && pipelines.results
    ? [
      {
        value: "all",
        label: "All Pipelines",
      },
      ...pipelines.results
        .map((p: any) => ({
          value: p.id,
          label: p.label,
        })),
    ]
    : []

  const {
    data: dealstageAvgTimes,
    // error: dealstageAvgTimesError,
    // isLoading: isLoadingdealstageAvgTimes,
  } = useSWR(
    [
      'query:dealstageAvgTimes',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([_, companyId]) => companyId
      ? getDealstageAvgTimesByCompanyId(parseInt(companyId, 10))
      : [],
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  let ownersForSelect = [{
    value: "all",
    label: "All Sales Reps",
  }];

  if (allOpenDeals && dealsForForecast) {
    const openDealOwners = allOpenDeals.map(d => getDealOwner(d));
    const forecastDealOwners = dealsForForecast.map(d => getDealOwner(d));

    const uniqueOwnersMap = new Map();
    openDealOwners.forEach(owner => {
      if (owner?.id && !uniqueOwnersMap.has(owner.id)) {
        uniqueOwnersMap.set(owner.id, owner);
      }
    });
    forecastDealOwners.forEach(owner => {
      if (owner?.id && !uniqueOwnersMap.has(owner.id)) {
        uniqueOwnersMap.set(owner.id, owner);
      }
    });
    const uniqueOwnersArray = Array.from(uniqueOwnersMap.values());

    ownersForSelect = [
      ...ownersForSelect,
      ...uniqueOwnersArray.map(owner => ({
        value: owner.id,
        label: `${owner.first_name} ${owner.last_name}`,
      }))
    ];
  }

  if (
    // isLoadingAllOpenDeals ||
    // !allOpenDeals ||
    // isLoadingDealsForForecast ||
    // !dealsForForecast
    isLoadingCurrencies ||
    !currentUser?.company
  ) return <Loader />;

  // if (
  //   allOpenDealsError ||
  //   dealsForForecastError
  // ) return <LoadingError />;

  // TODO: Create a nice fallback UI when HS isn't connected
  // if (hasHsToken === false) {
  //   return (
  //     <div className="flex flex-1 justify-center items-center">
  //       <p className="text-sm text-gray-500">
  //         Connect your Hubspot account to get started
  //       </p>
  //     </div>
  //   );
  // }

  const currenciesForRiskAssessment = (currencies ?? []) as Parameters<typeof prepareDealsForRiskAssessment>[2];
  const companyForRiskAssessment = currentUser.company as unknown as Parameters<typeof prepareDealsForRiskAssessment>[3];

  const hubspotDealsForPipelineRiskAndCoverage = prepareDealsForRiskAssessment(
    allOpenDeals ?? [],
    pipelines,
    currenciesForRiskAssessment,
    companyForRiskAssessment,
    dealstageAvgTimes,
  );

  const hubspotDealsForForecast = prepareDealsForRiskAssessment(
    dealsForForecast ?? [],
    pipelines,
    currenciesForRiskAssessment,
    companyForRiskAssessment,
    dealstageAvgTimes,
  );

  const {
    topRisk: topRiskPipelineRisk,
    bubbleChartData: chartDataPipelineBubble,
    riskExposureSummaryByStage,
    riskExposureDeals,
    concentratedDeals,
    pipelineConcentrationDealQty,
    pipelineConcentrationSummary,
    pipelineConcentrationShortSummary,
    highRiskDealsTotalValue,
  } = evaluateRiskPipelineRisk(
    hubspotDealsForPipelineRiskAndCoverage ?? [],
    currentUser?.company_id,
    pipelines?.results ?? [],
    "by_pipeline",
    globalSelectedPipeline,
    globalSelectedOwner,
    bubbleChartView,
    dealstageAvgTimes,
    companyCurrency,
  );

  const companyTarget = getCompanyTarget(currentUser?.company_id ?? 0);

  const {
    // revenueAtRisk,
    // revenueAtRiskTrendPct,
    // revenueAtRiskTrendIsGood,
    top3Risks,
  } = evaluateRiskForecast(
    hubspotDealsForForecast ?? [],
    currentUser?.company_id,
    pipelines?.results ?? [],
    forecastTimePeriod,
    globalSelectedPipeline,
    globalSelectedOwner,
    companyTarget,
    isForecastAccumulated,
    companyCurrency,
  );

  const chartDataForecast = canonicalForecast.chartData;

  const {
    summary: pipelineCoverageSummary,
    insight: pipelineCoverageInsight,
    coverage,
    // coverageChangePct,
    // coverageChangeIsGood,
    chartData: chartDataPipelineCoverage,
    isEarlyStageWeak: coverageIsEarlyStageWeak,
    isMidStageWeak: coverageIsMidStageWeak,
    isLateStageWeak: coverageIsLateStageWeak,
  } = evaluateRiskPipelineCoverage(
    hubspotDealsForPipelineRiskAndCoverage ?? [],
    currentUser?.company_id,
    [], // wonDealsForPipelineCoverageThisQuarter
    pipelines?.results ?? [],
    companyTarget,
    globalSelectedPipeline,
    globalSelectedOwner,
  );

  return (
    <>
      <div className="pt-2 space-y-4">
        {/* Global filters */}
        <div className="flex flex-row justify-end items-center gap-x-3">
          <p className="text-sm text-gray-500">Filters</p>
          <div className="flex flex-row gap-2">
            <div className="flex flex-row gap-2 items-center">
              <Select
                value={globalSelectedPipeline}
                onValueChange={v => setGlobalSelectedPipeline(v)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pipelinesForSelect.map((p: any) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-row gap-2 items-center">
              <Select
                value={globalSelectedOwner}
                onValueChange={v => setGlobalSelectedOwner(v)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ownersForSelect.map((owner: any) => (
                    <SelectItem key={owner.value} value={owner.value}>
                      {owner.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex flex-col xl:grid xl:grid-cols-3 gap-x-5 gap-y-5">
          {/* Cards */}
          <>
            <RiskHighlightCard
              title="Revenue at risk"
              headline={`${formatCurrencyValue(highRiskDealsTotalValue, companyCurrency, 1)}`}
              //change={revenueAtRiskTrendPct}
              //isChangeGood={revenueAtRiskTrendIsGood}
              //changeDescription="vs 30d ago"
              text={riskExposureSummaryByStage}
              body={
                highRiskDealsTotalValue > 0 && (
                  <RiskHighlightBadges>
                    {top3Risks.map(risk => (
                      <Fragment key={risk.risk}>
                        <Tooltip>
                          <TooltipTrigger>
                            <RiskHighlightBadge
                              dotColor="red"
                              text={mapRiskName(risk.risk as RiskIndicator)}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Open popup with deals that have highest risk score in this risk
                                setRevenueAtRiskViewingRisk(risk.risk as RiskIndicator);
                                setIsRevenueAtRiskDialogOpen(true);
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            <div className="max-w-60 space-y-3 text-sm text-gray-500">
                              {getRiskIndicatorDescription(risk.risk as RiskIndicator)
                                ?.split("\n\n")
                                .map((paragraph) => (
                                  <p key={paragraph}>{paragraph}</p>
                                ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </Fragment>
                    ))}
                  </RiskHighlightBadges>
                )
              }
              onClick={() => {
                setRevenueAtRiskViewingRisk(null);
                setIsRevenueAtRiskDialogOpen(true);
              }}
            />
            <RiskHighlightCard
              title="Pipeline coverage"
              headline={`${(coverage / 100).toFixed(1)}x`}
              //change={coverageChangePct}
              //isChangeGood={coverageChangeIsGood}
              //changeDescription="vs 30d ago"
              text={pipelineCoverageSummary}
              body={
                <RiskHighlightBadges>
                  <RiskHighlightBadge
                    text="Early stage"
                    dotColor={coverageIsEarlyStageWeak ? "red" : "green"}
                  />
                  <RiskHighlightBadge
                    text="Mid stage"
                    dotColor={coverageIsMidStageWeak ? "red" : "green"}
                  />
                  <RiskHighlightBadge
                    text="Late stage"
                    dotColor={coverageIsLateStageWeak ? "red" : "green"}
                  />
                </RiskHighlightBadges>
              }
            />
            <RiskHighlightCard
              title="Pipeline concentration"
              headline={pipelineConcentrationDealQty.toString()}
              text={pipelineConcentrationSummary}
              body={
                <RiskHighlightBadges>
                  {concentratedDeals.slice(0, 2).map(deal => (
                    <RiskHighlightBadge
                      key={deal.id}
                      dotColor="red"
                      text={deal.name}
                    />
                  ))}
                </RiskHighlightBadges>
              }
              onClick={() => setIsPipelineConcentrationDialogOpen(true)}
            />
          </>

          {/* Insight sections */}
          <SectionCard className="col-span-2">
            <div className="flex flex-col gap-y-4">
              <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                <div className="w-full flex flex-row justify-between mb-10">
                  {/* Header */}
                  <div>
                    <p className="font-medium text-sm">
                      Forecast to target
                    </p>
                    <p className="text-xs text-gray-500">
                      Modelled with 96% confidence
                    </p>
                  </div>
                  {/* Forecast toggles */}
                  <div className="flex flex-row gap-x-2 items-center">
                    <div className="flex flex-row gap-x-2 items-center">
                      <p className="text-sm text-gray-500">
                        Accumulated
                      </p>
                      <Switch
                        checked={isForecastAccumulated}
                        onCheckedChange={v => setIsForecastAccumuated(v)}
                      />
                    </div>
                    <Select
                      value={forecastTimePeriod}
                      onValueChange={v => setForecastTimePeriod(v as ForecastTimePeriod)}
                    >
                      <SelectTrigger className="h-8 w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem key="prev_quarter" value="prev_quarter">
                          Last quarter
                        </SelectItem>
                        <SelectItem key="this_quarter" value="this_quarter">
                          This quarter
                        </SelectItem>
                        <SelectItem key="next_quarter" value="next_quarter">
                          Next quarter
                        </SelectItem>
                        <SelectItem key="next_6_months" value="next_6_months">
                          Next 6 months
                        </SelectItem>
                        <SelectItem key="this_year" value="this_year">
                          This year
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Forecast chart */}
                <Link href="/revenue-intelligence/forecast">
                  <div className="pb-4">
                    {canonicalForecast.error ? <p role="alert">Forecast data could not be loaded.</p> : canonicalForecast.isLoading ? <Loader /> : <ForecastChart
                      data={chartDataForecast}
                      companyCurrency={companyCurrency}
                    />}
                  </div>
                </Link>
              </div>
            </div>
          </SectionCard>

          <SectionCard className="col-span-1">
            <div className="flex flex-col gap-y-4">
              <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                <div className="flex flex-row items-start justify-between">
                  {/* Header */}
                  <div className="pb-6">
                    <p className="font-medium text-sm">
                      Revenue at risk
                    </p>
                    <p className="text-xs text-gray-500">
                      Deals by stage and risk
                    </p>
                  </div>
                  <div>
                    <Select
                      value={bubbleChartView}
                      onValueChange={v => setBubbleChartView(v as any)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem key="largest" value="largest">
                          Largest
                        </SelectItem>
                        <SelectItem key="riskiest" value="riskiest">
                          Highest risk
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Pipeline risk chart */}
                <Link href="/revenue-intelligence/pipeline-risk">
                  <div>
                    <PipelineRiskBubbleChart
                      data={chartDataPipelineBubble}
                      companyCurrency={companyCurrency}
                    />
                    <AIInsightCard
                      text={`Systematic revenue risk is concentrated in ${mapRiskName(topRiskPipelineRisk?.risk as RiskIndicator ?? "stageProgression")}. Pipeline concentration is ${pipelineConcentrationShortSummary}.`}
                    />
                  </div>
                </Link>
              </div>
            </div>
          </SectionCard>

          <SectionCard className="col-span-1">
            <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
              <div className="w-full flex flex-row justify-between items-center pb-4">
                {/* Header */}
                <div>
                  <p className="font-medium text-sm">
                    Pipeline coverage
                  </p>
                  <p className="text-xs text-gray-500">
                    Coverage ratio vs quota
                  </p>
                </div>
              </div>

              {/* Pipeline coverage chart */}

              <Link href="/revenue-intelligence/pipeline-coverage">
                <div>
                  <PipelineCoverageChart
                    data={chartDataPipelineCoverage}
                  />
                  <AIInsightCard
                    text={pipelineCoverageInsight}
                  />
                </div>
              </Link>
            </div>
          </SectionCard>

          <SectionCard className="col-span-2 flex-1">
            <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
              <div className="w-full flex flex-row justify-between items-center pb-4">
                {/* Header */}
                <div>
                  <p className="font-medium text-sm">
                    Growth efficiency
                  </p>
                  <p className="text-xs text-gray-500">
                    Pipeline to revenue conversion
                  </p>
                </div>
              </div>

              {/* Growth efficiency chart and AI insight */}
              <div className="bg-gray-100 h-[406px] flex justify-center items-center bg-gradient-to-br from-[#9F86C0]/15 to-gray-50 rounded-[10px]">
                <p className="text-gray-400 font-display">
                  Coming soon
                </p>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Modal: Revenue at risk */}
      <Dialog
        open={isRevenueAtRiskDialogOpen}
        onOpenChange={v => setIsRevenueAtRiskDialogOpen(v)}
      >
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>
              Revenue at risk
            </DialogTitle>
            {revenueAtRiskViewingRisk !== null ? (
              <DialogDescription>
                These deals have the highest risk in "{mapRiskName(revenueAtRiskViewingRisk)}"
              </DialogDescription>
            ) : (
              <DialogDescription>
                These deals have the highest risk exposure
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogDescription className="max-h-[480px] overflow-y-scroll">
            {revenueAtRiskViewingRisk === null ? (
              <AtRiskDealsDataTable
                deals={riskExposureDeals}
                showTitle={false}
                showTopRisk
              />
            )
              : (
                <AtRiskDealsDataTable
                  deals={top3Risks
                    .find(risk => risk.risk === revenueAtRiskViewingRisk)
                    ?.deals ?? []
                  }
                  showTitle={false}
                  highlightIndicator={revenueAtRiskViewingRisk}
                />
              )
            }
          </DialogDescription>
        </DialogContent>
      </Dialog>

      {/* Modal: Pipeline concentration */}
      <Dialog
        open={isPipelineConcentrationDialogOpen}
        onOpenChange={v => setIsPipelineConcentrationDialogOpen(v)}
      >
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>
              Pipeline Concentration
            </DialogTitle>
            <DialogDescription>
              These are the largest deals in the pipeline
            </DialogDescription>
          </DialogHeader>
          <DialogDescription className="max-h-[480px] overflow-y-scroll">
            <AtRiskDealsDataTable
              deals={concentratedDeals}
              showTitle={false}
              showConcentration
              hideRiskColumn
            />
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
}
